import apiClient from './axios';
import { ApiResponse } from '../types';

/**
 * AI settings: which provider answers first, the model each action uses on
 * each provider, and the model catalogue with prices. Shapes are the backend's
 * (`GET /admin/ai/settings`); see payiq-backend `docs/admin-ai-settings-api.md`.
 */

export type ProviderKey = 'anthropic' | 'openai' | 'gemini' | 'grok';
export const PROVIDER_KEYS: ProviderKey[] = ['anthropic', 'openai', 'gemini', 'grok'];

export type CostTier = 'low' | 'medium' | 'high';
export type ActionCategory =
  | 'conversation'
  | 'writing'
  | 'classification'
  | 'extraction'
  | 'vision';

export interface ProviderStatus {
  key: ProviderKey;
  label: string;
  /** Cleared for customer data. A deploy setting, not editable here. */
  approved: boolean;
  /** Has its API key and a model on the server. */
  configured: boolean;
  /** 1-based place in the chain a request tries, or null when not in it. */
  position: number | null;
  /** Allowed to receive images. A deploy setting. */
  visionEnabled: boolean;
}

export interface RoutingState {
  defaultProvider: ProviderKey;
  fallbackOrder: ProviderKey[];
  /** False when the deploy settings apply. */
  fromAdmin: boolean;
  deploy: { defaultProvider: ProviderKey; fallbackOrder: ProviderKey[] };
  effectiveChain: ProviderKey[];
}

export type ModelSource = 'admin' | 'default' | 'tier-map' | 'unset';

export interface EffectiveModel {
  modelId: string | null;
  source: ModelSource;
  name?: string | null;
  tier?: CostTier | null;
  inCatalogue?: boolean;
}

export interface ActionUsage {
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

export interface AiAction {
  key: string;
  label: string;
  description: string;
  category: ActionCategory;
  needs: { tools: boolean; vision: boolean };
  source: string;
  /** Sends people's names, so only ever to Anthropic. */
  containsNames: boolean;
  usage: ActionUsage | null;
  models: Record<ProviderKey, EffectiveModel>;
}

export interface AiSettings {
  routing: RoutingState;
  providers: ProviderStatus[];
  tiers: { lowBelowUsd: number; highFromUsd: number };
  usageWindowDays: number;
  actions: AiAction[];
}

export interface CatalogueModel {
  id: string;
  provider: ProviderKey;
  providerLabel: string;
  modelId: string;
  name: string;
  inputUsdPerMTok: number;
  outputUsdPerMTok: number;
  cachedInputUsdPerMTok: number | null;
  blendedUsdPerMTok: number;
  tier: CostTier;
  supportsVision: boolean;
  supportsTools: boolean;
  reasoningEffort: string | null;
  active: boolean;
  priceNote: string | null;
  sourceUrl: string | null;
  updatedAt: string | null;
}

export interface ChangeNote {
  level: 'info' | 'warning';
  message: string;
}

export interface HighCostWarning {
  level: 'high';
  message: string;
  estimate: {
    basis: string;
    calls: number;
    proposedUsd: number;
    currentUsd: number | null;
  } | null;
}

export interface SetActionModelResult {
  before: string | null;
  after: string | null;
  tier: CostTier | null;
  warning: HighCostWarning | null;
  notes: ChangeNote[];
}

export interface SetRoutingResult {
  before: { defaultProvider: string; fallbackOrder: string[] };
  after: { defaultProvider: string; fallbackOrder: string[] };
  notes?: ChangeNote[];
}

export interface ModelInput {
  provider: ProviderKey;
  modelId: string;
  name: string;
  inputUsdPerMTok: number;
  outputUsdPerMTok: number;
  cachedInputUsdPerMTok?: number;
  supportsVision: boolean;
  supportsTools: boolean;
  reasoningEffort?: string;
  priceNote?: string;
  sourceUrl?: string;
}

/** Only the fields being changed: the backend rejects unknown keys. */
export type ModelPatch = Partial<
  Omit<ModelInput, 'provider' | 'modelId'> & { active: boolean }
>;

export const aiSettingsApi = {
  async settings(): Promise<AiSettings> {
    const res = await apiClient.get<ApiResponse<AiSettings>>('/admin/ai/settings');
    return res.data.data;
  },

  async models(): Promise<CatalogueModel[]> {
    const res = await apiClient.get<ApiResponse<{ models: CatalogueModel[] }>>(
      '/admin/ai/models',
    );
    return res.data.data.models;
  },

  async setRouting(body: {
    defaultProvider: ProviderKey;
    fallbackOrder: ProviderKey[];
  }): Promise<SetRoutingResult> {
    const res = await apiClient.put<ApiResponse<SetRoutingResult>>(
      '/admin/ai/routing',
      body,
    );
    return res.data.data;
  },

  async clearRouting(): Promise<void> {
    await apiClient.delete('/admin/ai/routing');
  },

  /** `modelId: null` puts the action back on its default. */
  async setActionModel(
    actionKey: string,
    provider: ProviderKey,
    modelId: string | null,
  ): Promise<SetActionModelResult> {
    const res = await apiClient.put<ApiResponse<SetActionModelResult>>(
      `/admin/ai/actions/${encodeURIComponent(actionKey)}/model`,
      { provider, modelId },
    );
    return res.data.data;
  },

  async setTiers(body: { lowBelowUsd: number; highFromUsd: number }): Promise<void> {
    await apiClient.put('/admin/ai/tiers', body);
  },

  async createModel(body: ModelInput): Promise<CatalogueModel> {
    const res = await apiClient.post<ApiResponse<CatalogueModel>>('/admin/ai/models', body);
    return res.data.data;
  },

  async updateModel(id: string, body: ModelPatch): Promise<void> {
    await apiClient.patch(`/admin/ai/models/${id}`, body);
  },
};
